/**
 *  @fileoverview Functions to invalidate queries in the react-query cache across the application.
 *
 * To be used with invalidate.tsx; not to be exported for use in the rest of the application.
 */
import type { StudioRequestSubject } from 'venn-state';
import { VennQueryClient } from './vennQueryClient';
import {
  BULK_GET_FUND_KEY_PREFIX,
  type BulkGetFundKey,
  COMPOSITE_BENCHMARK_KEY_PREFIX,
  FPI_KEY_PREFIX,
  type fpiKey,
  FUND_RETURNS_RANGE_KEY_PREFIX,
  type FundReturnsRangeKey,
  GET_FUND_KEY_PREFIX,
  type GetFundKey,
  HOLDINGS_BREAKDOWN_KEY_PREFIX,
  type HoldingsCachedKey,
  INVESTMENT_PROXY_METADATA_KEY_PREFIX,
  INVESTMENT_RANGE_ANALYSIS_KEY_PREFIX,
  PORTFOLIOS_LIBRARY_KEY_PREFIX,
  PROXY_FEASIBILITY_KEY_PREFIX,
  PROXY_METADATA_KEY_PREFIX,
  PROXY_METRICS_KEY_PREFIX,
  type ProxyFeasibilityKey,
  type ProxyMetadataKey,
  type ProxyMetricsKey,
  RANGE_ANALYSIS_KEY_PREFIX,
  type RangeAnalysisKey,
  STUDIO_ANALYSIS_KEY_PREFIX,
  type StudioAnalysisKey,
  TOP_HOLDINGS_KEY_PREFIX,
  type TopHoldingsKey,
} from './query-keys';
import { findInPortfolioBy } from '../portfolio/portfolioUtils';
import type { AnalysisSubject } from '../analysis';
import type { QueryClient } from '@tanstack/react-query';

export function invalidateForecast() {
  const queryClient = VennQueryClient.getInstance();

  return queryClient.invalidateQueries({
    queryKey: [STUDIO_ANALYSIS_KEY_PREFIX],
  });
}

export function invalidateInvestment(investmentId: string | number) {
  return invalidateInvestments([investmentId]);
}

/**
 * All supported invalidation targets grouped by category.
 */
export type FundInvalidationTarget =
  | typeof GET_FUND_KEY_PREFIX
  | typeof BULK_GET_FUND_KEY_PREFIX
  | typeof FUND_RETURNS_RANGE_KEY_PREFIX
  | typeof INVESTMENT_RANGE_ANALYSIS_KEY_PREFIX
  | typeof INVESTMENT_PROXY_METADATA_KEY_PREFIX
  | typeof PROXY_METRICS_KEY_PREFIX;

export type GeneralInvalidationTarget =
  | typeof RANGE_ANALYSIS_KEY_PREFIX
  | typeof PROXY_METADATA_KEY_PREFIX
  | typeof STUDIO_ANALYSIS_KEY_PREFIX
  | typeof PROXY_FEASIBILITY_KEY_PREFIX;

export type HoldingsInvalidationTarget =
  | typeof HOLDINGS_BREAKDOWN_KEY_PREFIX
  | typeof TOP_HOLDINGS_KEY_PREFIX
  | typeof FPI_KEY_PREFIX;

export type InvestmentInvalidationTarget =
  | FundInvalidationTarget
  | GeneralInvalidationTarget
  | HoldingsInvalidationTarget;

export type InvalidateInvestmentsOptions = {
  queries?: readonly InvestmentInvalidationTarget[]; // defaults to all
};

/**
 * Invalidate active queries that are affected by the given investment(s).
 */
export function invalidateInvestments(investmentIds: (string | number)[], options: InvalidateInvestmentsOptions = {}) {
  const { queries } = options;

  const investmentIdsSet = new Set<string>(investmentIds.map(String));
  const shouldInvalidate = getSubjectMatchPredicate(investmentIdsSet);
  const queryClient = VennQueryClient.getInstance();

  // TODO: continue migrating useQuery usage to standardized keys and add to invalidation as needed:
  // - EXPORT_METADATA_KEY
  // - COMPARISON_CACHE_KEY

  // -- FUND INVALIDATIONS --
  const fundInvalidations: Record<FundInvalidationTarget, () => Promise<void>> = {
    [GET_FUND_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_FUND_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as GetFundKey;
          return investmentIdsSet.has(queryKey[1]);
        },
      }),

    [BULK_GET_FUND_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [BULK_GET_FUND_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as BulkGetFundKey;
          return queryKey[1].some((fundId) => investmentIdsSet.has(fundId));
        },
      }),

    [FUND_RETURNS_RANGE_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [FUND_RETURNS_RANGE_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as FundReturnsRangeKey;
          return investmentIdsSet.has(queryKey[1]);
        },
      }),

    [INVESTMENT_RANGE_ANALYSIS_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [INVESTMENT_RANGE_ANALYSIS_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as FundReturnsRangeKey;
          return investmentIdsSet.has(queryKey[1]);
        },
      }),

    [INVESTMENT_PROXY_METADATA_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [INVESTMENT_PROXY_METADATA_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as FundReturnsRangeKey;
          return investmentIdsSet.has(queryKey[1]);
        },
      }),

    [PROXY_METRICS_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [PROXY_METRICS_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as ProxyMetricsKey;
          return investmentIdsSet.has(queryKey[1]);
        },
      }),
  };

  // -- GENERAL INVALIDATIONS --
  const generalInvalidations: Record<GeneralInvalidationTarget, () => Promise<unknown>> = {
    [RANGE_ANALYSIS_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [RANGE_ANALYSIS_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as RangeAnalysisKey;
          return shouldInvalidate(queryKey[1]);
        },
      }),

    [PROXY_METADATA_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [PROXY_METADATA_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as ProxyMetadataKey;
          return queryKey[1].some((subjectId) => investmentIdsSet.has(subjectId.id));
        },
      }),

    [STUDIO_ANALYSIS_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [STUDIO_ANALYSIS_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as StudioAnalysisKey;
          return queryKey[2].some(
            (request) => shouldInvalidate(request.subject) || shouldInvalidate(request.benchmark),
          );
        },
      }),

    [PROXY_FEASIBILITY_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [PROXY_FEASIBILITY_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as ProxyFeasibilityKey;
          return investmentIdsSet.has(queryKey[1]);
        },
      }),
  };

  // -- HOLDINGS INVALIDATIONS --
  const holdingsInvalidations: Record<HoldingsInvalidationTarget, () => Promise<unknown>> = {
    [HOLDINGS_BREAKDOWN_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [HOLDINGS_BREAKDOWN_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as HoldingsCachedKey;
          return queryKey[2].some(
            (subject) =>
              (subject.investment && investmentIdsSet.has(subject.investment)) ||
              (subject.portfolio?.id && investmentIdsSet.has(String(subject.portfolio.id))),
          );
        },
      }),

    [TOP_HOLDINGS_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [TOP_HOLDINGS_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as TopHoldingsKey;
          return queryKey[1].some(
            (subject) =>
              (subject.investment && investmentIdsSet.has(subject.investment)) ||
              (subject.portfolio?.id && investmentIdsSet.has(String(subject.portfolio.id))),
          );
        },
      }),

    [FPI_KEY_PREFIX]: () =>
      queryClient.invalidateQueries({
        queryKey: [FPI_KEY_PREFIX],
        predicate: (query) => {
          const queryKey = query.queryKey as fpiKey;
          return queryKey[1].some(
            (subject) =>
              (subject.investment && investmentIdsSet.has(subject.investment)) ||
              (subject.portfolio?.id && investmentIdsSet.has(String(subject.portfolio.id))),
          );
        },
      }),
  };

  // flatten selected invalidations
  const allInvalidations = {
    ...fundInvalidations,
    ...generalInvalidations,
    ...holdingsInvalidations,
  };

  const selected = queries ? queries.map((q) => allInvalidations[q]) : Object.values(allInvalidations);

  return Promise.allSettled(selected.map((fn) => fn()));
}

export function invalidateInvestmentsHoldingsProxy(investmentIds: (string | number)[]) {
  return invalidateInvestments(investmentIds, {
    queries: [
      GET_FUND_KEY_PREFIX,
      BULK_GET_FUND_KEY_PREFIX,
      INVESTMENT_PROXY_METADATA_KEY_PREFIX,
      PROXY_METRICS_KEY_PREFIX,

      PROXY_METADATA_KEY_PREFIX,
      STUDIO_ANALYSIS_KEY_PREFIX,
      PROXY_FEASIBILITY_KEY_PREFIX,

      HOLDINGS_BREAKDOWN_KEY_PREFIX,
      TOP_HOLDINGS_KEY_PREFIX,
      FPI_KEY_PREFIX,
    ],
  });
}

/**
 * @param investmentIdsSet Set of investment IDs to match against.
 * @returns Predicate function that returns true if a match is found against the subject, the subject's benchmark, or any of the subject's portfolio children.
 */
function getSubjectMatchPredicate(investmentIdsSet: Set<string>) {
  return (subject: StudioRequestSubject | AnalysisSubject | undefined) => {
    if (!subject) return false;

    const potentialKeys: (string | undefined | false)[] = [
      String(subject.id),
      subject.fund?.id,
      'individualBenchmark' in subject &&
        (subject.individualBenchmark?.fundId || String(subject.individualBenchmark?.portfolioId)),
      subject.fund?.proxyId,
      'activeBenchmarkId' in subject && typeof subject.activeBenchmarkId === 'string' && subject.activeBenchmarkId,
    ];
    if (potentialKeys.some((key) => key && investmentIdsSet.has(key))) {
      return true;
    }
    const portfolioNode =
      subject.strategy ?? ('modifiedPortfolio' in subject ? subject.modifiedPortfolio : undefined) ?? subject.portfolio;

    return !!(
      portfolioNode &&
      findInPortfolioBy(
        portfolioNode,
        (portfolio) => investmentIdsSet.has(String(portfolio.fund?.id)) || investmentIdsSet.has(String(portfolio.id)),
      )
    );
  };
}

export const invalidatePortfoliosLibrary = (hasMyDataFF: boolean, queryClient: QueryClient) => {
  if (hasMyDataFF) {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === PORTFOLIOS_LIBRARY_KEY_PREFIX,
    });
  }
};

export const invalidateCompositeBenchmark = (fundId: string) => {
  const queryClient = VennQueryClient.getInstance();
  return queryClient.invalidateQueries({
    queryKey: [COMPOSITE_BENCHMARK_KEY_PREFIX, fundId],
  });
};
